import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface DescriptionAnalysisItem {
  analysis_id: number
  user_id: number
  user_name: string
  entry_id: number
  start_time: string
  end_time: string
  description: string
  tags: string
  billable: number
  task_name: string
  duration_seconds: number
  issue: string
  rating: number
  suggestion: string
  profile_picture: string | null
}

export interface DescriptionAnalysisResponse {
  success: boolean
  page: number
  limit: number
  total: number
  data: DescriptionAnalysisItem[]
}

export interface BotAlertItem {
  user_id: number
  user_name: string
  missing_days_count: string
  alert_date: string
  profile_picture: string | null
}

export interface BotAlertPagination {
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface BotAlertResponse {
  data: BotAlertItem[]
  pagination: BotAlertPagination
}

export interface BotAlertParams {
  page?: number | undefined
  limit?: number | undefined
  search?: string | undefined
  team_id?: number | undefined
  startDate?: string | undefined
  endDate?: string | undefined
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}

export interface DescriptionAnalysisParams {
  page?: number | undefined
  limit?: number | undefined
  search?: string | undefined
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}

export interface FlaggedEntryDetail {
  entry_id: number
  date: string
  task_name: string
  tag_used: string
  hours: number
  issue: string
  rating: number
  suggestion: string
}

export interface WeeklyDescriptionEntry {
  entry_id: number
  date: string
  description: string
  task_name: string
  tags: string
  billable: number
  duration_seconds: number
  issue: string
  rating: number
  suggestion: string
}

export interface WeeklySummaryItem {
  user_id: number
  user_name: string
  team: string | null
  total_hours: number
  hours_target: number
  total_entries?: number
  flagged_entries: number
  hours_score: number
  description_score: number
  tag_score: number
  billability_score: number
  composite_score?: number
  final_score: number
  pass: boolean
  hours_hard_fail: boolean
  good_points: string[]
  bad_points: string[]
  critical_flags: string[]
  flagged_entry_details: FlaggedEntryDetail[]
  description_analysis: WeeklyDescriptionEntry[]
  clickup_message: string | null
  source?: 'ai_audit' | 'estimated'
}

export interface WeeklySummaryResponse {
  success: boolean
  page: number
  limit: number
  total: number
  data: WeeklySummaryItem[]
}

export interface WeeklySummaryParams {
  week_start: string
  week_end: string
  page?: number
  limit?: number
  search?: string
  team_id?: number | undefined
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}

export interface UserAlertDetail {
  alert_date: string
  missing_days_count: number
}

export interface UserBotAlertDetailResponse {
  user_id: number
  alerts: UserAlertDetail[]
  pagination: BotAlertPagination
}

export interface UserBotAlertDetailParams {
  userId: number
  page?: number | undefined
  limit?: number | undefined
  startDate?: string | undefined
  endDate?: string | undefined
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
}

export const botApi = createApi({
  reducerPath: 'botApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/bot`,
    prepareHeaders: prepareHeaders,
  }),
  endpoints: builder => ({
    getDescription: builder.query<
      DescriptionAnalysisResponse,
      DescriptionAnalysisParams
    >({
      query: ({ page = 1, limit = 20, search = '', sort_by, order }) => ({
        url: '/description-analysis',
        params: { page, limit, search, sort_by, order },
      }),
    }),
    getBotAlerts: builder.query<BotAlertResponse, BotAlertParams>({
      query: ({
        page = 1,
        limit = 10,
        search = '',
        team_id,
        startDate,
        endDate,
        sort_by,
        order,
      }) => ({
        url: '/alerts',
        params: {
          page,
          limit,
          search,
          ...(team_id !== undefined && { team_id }),
          startDate,
          endDate,
          sort_by,
          order,
        },
      }),
    }),
    getUserBotAlertDetails: builder.query<
      UserBotAlertDetailResponse,
      UserBotAlertDetailParams
    >({
      query: ({
        userId,
        page = 1,
        limit = 10,
        startDate,
        endDate,
        sort_by,
        order,
      }) => ({
        url: `/alerts/${userId}`,
        params: { page, limit, startDate, endDate, sort_by, order },
      }),
    }),
    getWeeklySummary: builder.query<WeeklySummaryResponse, WeeklySummaryParams>(
      {
        query: ({
          week_start,
          week_end,
          page = 1,
          limit = 20,
          search = '',
          team_id,
          sort_by,
          order,
        }) => ({
          url: '/weekly-summary',
          params: {
            week_start,
            week_end,
            page,
            limit,
            search,
            ...(team_id !== undefined && { team_id }),
            sort_by,
            order,
          },
        }),
      }
    ),
  }),
})

export const {
  useGetDescriptionQuery,
  useGetBotAlertsQuery,
  useGetUserBotAlertDetailsQuery,
  useGetWeeklySummaryQuery,
} = botApi
